import React, { useState, useEffect } from 'react';
import { Text, Icon, Button, Card, Heading, Badge } from '../ui/index.jsx';

/**
 * Confetti effect component
 */
function Confetti() {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const colors = ['#7B61FF', '#BA87FF', '#22C55E', '#3B82F6', '#F59E0B', '#EC4899'];
    const newParticles = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 2,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: Math.random() * 8 + 4,
      duration: Math.random() * 2 + 2
    }));
    setParticles(newParticles);
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute animate-fall"
          style={{
            left: `${particle.x}%`,
            top: '-20px',
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            backgroundColor: particle.color,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            animationDelay: `${particle.delay}s`,
            animationDuration: `${particle.duration}s`
          }}
        />
      ))}
      <style>{`
        @keyframes fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-fall {
          animation: fall linear forwards;
        }
      `}</style>
    </div>
  );
}

/**
 * Feature card for next steps
 */
function NextStepCard({ icon, title, description, action, actionLabel, variant = 'default' }) {
  const bgColors = {
    default: 'bg-[#1A1A24] border-[#2A2A3C] hover:border-[#3A3A4C]',
    primary: 'bg-[#7B61FF]/10 border-[#7B61FF]/30 hover:border-[#7B61FF]/50'
  };

  return (
    <Card className={`p-4 ${bgColors[variant]} transition-colors cursor-pointer`} onClick={action}>
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
          variant === 'primary' ? 'bg-[#7B61FF]' : 'bg-[#2A2A3C]'
        }`}>
          <Icon name={icon} size="sm" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <Text className="font-medium text-white">{title}</Text>
          <Text className="text-sm text-gray-400 mt-1">{description}</Text>
        </div>
        <Icon name="chevron-right" size="sm" className="text-gray-500 mt-3" />
      </div>
    </Card>
  );
}

/**
 * DeploySuccess component - Success screen after deployment
 */
export default function DeploySuccess({ deployedUrl, githubUrl, sessionData, onViewDashboard }) {
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Stop confetti after 5 seconds
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {showConfetti && <Confetti />}

      <div className="text-center mb-8">
        {/* Success icon */}
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 mb-6">
          <div className="w-14 h-14 rounded-full bg-green-500 flex items-center justify-center">
            <Icon name="check" size="xl" className="text-white" />
          </div>
        </div>

        <Heading level={1} className="text-white mb-2">
          Your App is Live!
        </Heading>
        <Text className="text-gray-400 text-lg">
          Congratulations! Your application has been deployed successfully.
        </Text>
      </div>

      {/* Main links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {/* Live App Card */}
        <Card className="bg-gradient-to-br from-[#7B61FF]/20 to-[#BA87FF]/10 border-[#7B61FF]/30 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[#7B61FF] flex items-center justify-center">
              <Icon name="globe" size="md" className="text-white" />
            </div>
            <div>
              <Text className="font-semibold text-white">Live Application</Text>
              <Text className="text-sm text-gray-400">Your app is now accessible</Text>
            </div>
          </div>

          <div className="bg-[#0D0D10] rounded-lg px-4 py-3 mb-4">
            <Text className="text-[#BA87FF] font-mono text-sm break-all">{deployedUrl}</Text>
          </div>

          <Button
            variant="primary"
            className="w-full"
            onClick={() => window.open(deployedUrl, '_blank')}
          >
            <Icon name="external-link" size="sm" className="mr-2" />
            Open Your App
          </Button>
        </Card>

        {/* GitHub Card */}
        <Card className="bg-[#1A1A24] border-[#2A2A3C] p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[#2A2A3C] flex items-center justify-center">
              <Icon name="github" size="md" className="text-white" />
            </div>
            <div>
              <Text className="font-semibold text-white">GitHub Repository</Text>
              <Text className="text-sm text-gray-400">Your source code</Text>
            </div>
          </div>

          <div className="bg-[#0D0D10] rounded-lg px-4 py-3 mb-4">
            <Text className="text-gray-400 font-mono text-sm break-all">{githubUrl}</Text>
          </div>

          <Button
            variant="secondary"
            className="w-full"
            onClick={() => window.open(githubUrl, '_blank')}
          >
            <Icon name="github" size="sm" className="mr-2" />
            View Repository
          </Button>
        </Card>
      </div>

      {/* What's included */}
      <Card className="bg-[#1A1A24] border-[#2A2A3C] p-6 mb-8">
        <Heading level={3} className="text-white mb-4">
          What's Included
        </Heading>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: 'code', label: 'Source Code', desc: 'Complete codebase' },
            { icon: 'database', label: 'D1 Database', desc: 'SQLite in the cloud' },
            { icon: 'key', label: 'KV Storage', desc: 'Key-value store' },
            { icon: 'git-branch', label: 'CI/CD', desc: 'GitHub Actions' }
          ].map((item) => (
            <div key={item.label} className="text-center p-4 bg-[#0D0D10] rounded-lg">
              <div className="w-10 h-10 rounded-lg bg-[#2A2A3C] flex items-center justify-center mx-auto mb-2">
                <Icon name={item.icon} size="sm" className="text-[#7B61FF]" />
              </div>
              <Text className="font-medium text-white text-sm">{item.label}</Text>
              <Text className="text-xs text-gray-500">{item.desc}</Text>
            </div>
          ))}
        </div>
      </Card>

      {/* Next steps */}
      <div className="mb-8">
        <Heading level={3} className="text-white mb-4">
          Next Steps
        </Heading>
        <div className="space-y-3">
          <NextStepCard
            icon="edit-3"
            title="Chat & Modify"
            description="Make changes to your app using natural language"
            actionLabel="Start Chat"
            variant="primary"
            action={() => {/* TODO: Navigate to chat modify */}}
          />
          <NextStepCard
            icon="users"
            title="Invite Team Members"
            description="Add collaborators to your GitHub repository"
            action={() => window.open(`${githubUrl}/settings/access`, '_blank')}
          />
          <NextStepCard
            icon="settings"
            title="Configure Domain"
            description="Add a custom domain to your Cloudflare Pages project"
            action={() => window.open('https://dash.cloudflare.com', '_blank')}
          />
          <NextStepCard
            icon="book-open"
            title="View Documentation"
            description="Learn how to extend and customize your application"
            action={() => {/* TODO: Link to docs */}}
          />
        </div>
      </div>

      {/* Footer actions */}
      <div className="flex justify-center gap-4">
        <Button
          variant="secondary"
          onClick={onViewDashboard}
        >
          <Icon name="layout-dashboard" size="sm" className="mr-2" />
          Back to Dashboard
        </Button>
        <Button
          variant="primary"
          onClick={() => window.location.href = '/'}
        >
          <Icon name="plus" size="sm" className="mr-2" />
          Build Another App
        </Button>
      </div>

      {/* App info footer */}
      {sessionData && (
        <Card className="bg-[#0D0D10] border-[#2A2A3C] p-4 mt-8">
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-xs text-gray-500">App Idea</Text>
              <Text className="text-sm text-gray-300 mt-1">
                {sessionData.idea?.substring(0, 150)}
                {sessionData.idea?.length > 150 ? '...' : ''}
              </Text>
            </div>
            <Badge variant="success">Deployed</Badge>
          </div>
        </Card>
      )}
    </>
  );
}
